import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AlertTriangle, Wrench, ArrowLeft, Loader2 } from "lucide-react";
import { authApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

/**
 * Returning from Emergent OAuth.
 * URL fragment looks like:  /auth/callback#session_id=<id>
 * - We extract session_id synchronously
 * - POST it to our backend together with any pending invite_token from sessionStorage
 * - Backend mints our JWT cookies, returns the user; we redirect to /
 *
 * Per the playbook we use useRef (not useState) to guard against StrictMode
 * double-effect runs that would consume the session_id twice.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const { goBack } = useNavigationHistory();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setError("Missing session_id — the OAuth redirect was incomplete. Please try signing in again.");
      return;
    }
    const inviteToken = sessionStorage.getItem("pending_invite_token") || undefined;
    const companyName = sessionStorage.getItem("pending_company_name") || undefined;
    const signupPrefill = sessionStorage.getItem("pending_signup_prefill");

    (async () => {
      try {
        if (signupPrefill) {
          // /signup flow: resolve identity only — no account is created yet.
          const data = await authApi.googlePrefill(sessionId);
          sessionStorage.removeItem("pending_signup_prefill");
          sessionStorage.setItem("google_prefill_data", JSON.stringify(data));
          window.history.replaceState({}, "", "/auth/callback");
          navigate("/signup", { replace: true });
          return;
        }
        await authApi.googleSession(sessionId, inviteToken, companyName);
        // Clean up state then refresh the AuthContext so the rest of the app sees the new user.
        sessionStorage.removeItem("pending_invite_token");
        sessionStorage.removeItem("pending_company_name");
        // Wipe the fragment so a refresh doesn't reprocess the (now-spent) session_id.
        window.history.replaceState({}, "", "/auth/callback");
        const u = await refreshMe();
        // Trigger the "clock in?" prompt for tenant users (never for platform admins).
        if (u && u.role && u.role !== "super_admin") {
          try { sessionStorage.setItem("clockin_prompt_pending", "1"); } catch (_) { /* private mode */ }
        }
        navigate("/", { replace: true });
      } catch (err) {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      }
    })();
  }, [navigate, refreshMe]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
            <Wrench className="h-4.5 w-4.5 text-white" />
          </div>
          <div className="font-bold text-slate-900">ServicePro</div>
        </div>

        {!error ? (
          <div data-testid="auth-callback-loading" className="flex items-start gap-3">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin mt-0.5" />
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Signing you in…</div>
              <div className="text-[12.5px] text-slate-500 mt-0.5">
                Verifying your Google account with ServicePro.
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="auth-callback-error" className="space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="text-[22px] font-bold text-slate-900">Sign-in failed</h2>
            <p className="text-[13.5px] text-slate-600 leading-relaxed">{error}</p>
            <button
              type="button"
              onClick={() => goBack("/login")}
              data-testid="auth-callback-back"
              className="inline-flex items-center gap-1.5 text-[13px] text-blue-600 hover:text-blue-700 font-semibold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
